import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Package, Truck, Users, Warehouse, Thermometer, Scale, 
  MapPin, Plus, Search, Filter, BarChart3, AlertTriangle,
  CheckCircle, Clock, ArrowRight, Leaf, FileText, QrCode,
  Calendar, Route, TrendingUp, Box, Layers
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs du module
const COULEURS = {
  clients: "#22c55e",
  fournisseurs: "#3b82f6", 
  prospects: "#f97316",
  alertes: "#ef4444",
  bio: "#22c55e",
  zrp: "#84cc16",
  globalgap: "#0ea5e9",
  hve: "#10b981",
  conventionnel: "#6b7280"
};

const CERTIFICATIONS = [
  { code: 'bio', nom: 'Bio AB', couleur: '#22c55e', icon: Leaf },
  { code: 'zrp', nom: 'Zéro Résidu', couleur: '#84cc16', icon: Leaf },
  { code: 'globalgap', nom: 'GlobalGAP', couleur: '#0ea5e9', icon: CheckCircle },
  { code: 'hve', nom: 'HVE', couleur: '#10b981', icon: Leaf },
  { code: 'conventionnel', nom: 'Conventionnel', couleur: '#6b7280', icon: Box }
];

// Composant Badge Certification
const CertificationBadge = ({ certification }) => {
  const cert = CERTIFICATIONS.find(c => c.code === certification) || CERTIFICATIONS[4];
  return (
    <Badge 
      variant="outline" 
      style={{ borderColor: cert.couleur, color: cert.couleur }}
      className="text-xs"
    >
      {cert.nom}
    </Badge>
  );
};

// Dashboard principal
const DashboardExpediteur = ({ stats, alertes }) => {
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.fournisseurs_actifs || 0}</p>
                <p className="text-xs text-muted-foreground">Fournisseurs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Package className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.receptions_jour || 0}</p>
                <p className="text-xs text-muted-foreground">Réceptions jour</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Scale className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{((stats?.tonnage_jour_kg || 0) / 1000).toFixed(1)}t</p>
                <p className="text-xs text-muted-foreground">Tonnage jour</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Thermometer className="w-5 h-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.taux_remplissage_frigo || 0}%</p>
                <p className="text-xs text-muted-foreground">Frigos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Truck className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.expeditions_en_cours || 0}</p>
                <p className="text-xs text-muted-foreground">Expéditions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertes */}
      {alertes && alertes.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-4 h-4" />
              Alertes ({alertes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertes.map((alerte, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <span>{alerte.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Liste Fournisseurs
const FournisseursList = ({ fournisseurs, onSelect, onAdd }) => {
  const [search, setSearch] = useState('');
  const [filterCert, setFilterCert] = useState('all');

  const filtered = fournisseurs.filter(f => {
    const matchSearch = f.nom_entreprise?.toLowerCase().includes(search.toLowerCase()) ||
                       f.contact_nom?.toLowerCase().includes(search.toLowerCase());
    const matchCert = filterCert === 'all' || f.certifications?.includes(filterCert);
    return matchSearch && matchCert;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Rechercher un fournisseur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterCert} onValueChange={setFilterCert}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Certification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {CERTIFICATIONS.map(c => (
              <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={onAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Fournisseur
        </Button>
      </div>

      <div className="grid gap-3">
        {filtered.map(f => (
          <Card 
            key={f.id} 
            className="cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => onSelect(f)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: COULEURS.fournisseurs }}
                  >
                    {f.nom_entreprise?.charAt(0) || 'F'}
                  </div>
                  <div>
                    <p className="font-medium">{f.nom_entreprise}</p>
                    <p className="text-sm text-muted-foreground">{f.contact_nom} {f.contact_prenom}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {f.certifications?.map(cert => (
                    <CertificationBadge key={cert} certification={cert} />
                  ))}
                  <Badge variant={f.statut === 'actif' ? 'default' : 'secondary'}>
                    {f.statut}
                  </Badge>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Tonnage livré:</span>
                  <span className="ml-2 font-medium">{((f.tonnage_total_livre || 0) / 1000).toFixed(1)}t</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Score:</span>
                  <span className="ml-2 font-medium">{f.score_fiabilite || 100}/100</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Déchets moy:</span>
                  <span className="ml-2 font-medium">{f.taux_dechets_moyen || 0}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Formulaire nouveau fournisseur
const FournisseurForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    nom_entreprise: '',
    contact_nom: '',
    contact_prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    siret: '',
    certifications: [],
    notes: ''
  });

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Entreprise *</label>
          <Input 
            value={formData.nom_entreprise}
            onChange={(e) => setFormData({...formData, nom_entreprise: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium">SIRET</label>
          <Input 
            value={formData.siret}
            onChange={(e) => setFormData({...formData, siret: e.target.value})}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Nom contact *</label>
          <Input 
            value={formData.contact_nom}
            onChange={(e) => setFormData({...formData, contact_nom: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium">Prénom</label>
          <Input 
            value={formData.contact_prenom}
            onChange={(e) => setFormData({...formData, contact_prenom: e.target.value})}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Email</label>
          <Input 
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Téléphone</label>
          <Input 
            value={formData.telephone}
            onChange={(e) => setFormData({...formData, telephone: e.target.value})}
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Adresse</label>
        <Input 
          value={formData.adresse}
          onChange={(e) => setFormData({...formData, adresse: e.target.value})}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Code Postal</label>
          <Input 
            value={formData.code_postal}
            onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Ville</label>
          <Input 
            value={formData.ville}
            onChange={(e) => setFormData({...formData, ville: e.target.value})}
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Certifications</label>
        <div className="flex flex-wrap gap-2">
          {CERTIFICATIONS.map(cert => (
            <Button
              key={cert.code}
              type="button"
              variant={formData.certifications.includes(cert.code) ? "default" : "outline"}
              size="sm"
              onClick={() => toggleCertification(cert.code)}
              style={formData.certifications.includes(cert.code) ? { backgroundColor: cert.couleur } : {}}
            >
              {cert.nom}
            </Button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Notes</label>
        <Input 
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
        <Button type="submit">Créer le fournisseur</Button>
      </div>
    </form>
  );
};

// Liste Réceptions
const ReceptionsList = ({ receptions, onNewReception }) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Réceptions du jour</h3>
        <Button onClick={onNewReception}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle réception
        </Button>
      </div>

      <div className="space-y-2">
        {receptions.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune réception aujourd'hui</p>
          </Card>
        ) : (
          receptions.map(r => (
            <Card key={r.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <Package className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium">{r.numero_lot}</p>
                      <p className="text-sm text-muted-foreground">
                        {r.fournisseur_nom} • {r.variete}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <CertificationBadge certification={r.certification} />
                    <div className="text-right">
                      <p className="font-bold">{r.poids_brut_kg} kg</p>
                      <p className="text-xs text-muted-foreground">{r.statut}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Formulaire Réception
const ReceptionForm = ({ fournisseurs, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    fournisseur_id: '',
    espece: '',
    variete: '',
    certification: 'conventionnel',
    poids_brut_kg: '',
    qualite_visuelle: 'A',
    brix: '',
    temperature_arrivee: '',
    transporteur: '',
    numero_bon_livraison: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({...formData, poids_brut_kg: parseFloat(formData.poids_brut_kg)});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium">Fournisseur *</label>
        <Select value={formData.fournisseur_id} onValueChange={(v) => setFormData({...formData, fournisseur_id: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner un fournisseur" />
          </SelectTrigger>
          <SelectContent>
            {fournisseurs.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.nom_entreprise}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Espèce *</label>
          <Select value={formData.espece} onValueChange={(v) => setFormData({...formData, espece: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Espèce" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="abricot">Abricot</SelectItem>
              <SelectItem value="peche">Pêche</SelectItem>
              <SelectItem value="pomme">Pomme</SelectItem>
              <SelectItem value="poire">Poire</SelectItem>
              <SelectItem value="cerise">Cerise</SelectItem>
              <SelectItem value="raisin">Raisin</SelectItem>
              <SelectItem value="tomate">Tomate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Variété *</label>
          <Input 
            value={formData.variete}
            onChange={(e) => setFormData({...formData, variete: e.target.value})}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Certification</label>
          <Select value={formData.certification} onValueChange={(v) => setFormData({...formData, certification: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CERTIFICATIONS.map(c => (
                <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Poids brut (kg) *</label>
          <Input 
            type="number"
            value={formData.poids_brut_kg}
            onChange={(e) => setFormData({...formData, poids_brut_kg: e.target.value})}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-medium">Qualité visuelle</label>
          <Select value={formData.qualite_visuelle} onValueChange={(v) => setFormData({...formData, qualite_visuelle: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="A">A - Excellent</SelectItem>
              <SelectItem value="B">B - Bon</SelectItem>
              <SelectItem value="C">C - Passable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Brix (°)</label>
          <Input 
            type="number"
            step="0.1"
            value={formData.brix}
            onChange={(e) => setFormData({...formData, brix: e.target.value})}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Temp. arrivée (°C)</label>
          <Input 
            type="number"
            step="0.1"
            value={formData.temperature_arrivee}
            onChange={(e) => setFormData({...formData, temperature_arrivee: e.target.value})}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
        <Button type="submit">Réceptionner</Button>
      </div>
    </form>
  );
};

// Gestion Calibrage
const CalibrageSection = ({ receptions, calibres, onCalibrage }) => {
  const [selectedLot, setSelectedLot] = useState(null);
  const [sorties, setSorties] = useState({});
  const [heureDebut, setHeureDebut] = useState('');
  const [heureFin, setHeureFin] = useState('');

  const lotsACalerabrer = receptions.filter(r => r.statut === 'receptionne');

  const handleCalibrage = () => {
    if (!selectedLot) return;
    
    onCalibrage({
      lot_reception_id: selectedLot.id,
      sorties_calibres: sorties,
      heure_debut: heureDebut,
      heure_fin: heureFin
    });
    
    setSelectedLot(null);
    setSorties({});
  };

  return (
    <div className="space-y-4">
      <h3 className="font-medium flex items-center gap-2">
        <Layers className="w-5 h-5" />
        Calibrage
      </h3>

      {lotsACalerabrer.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucun lot à calibrer</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Lots à calibrer</p>
            {lotsACalerabrer.map(lot => (
              <Card 
                key={lot.id}
                className={`cursor-pointer transition-colors ${selectedLot?.id === lot.id ? 'border-primary' : 'hover:border-primary/50'}`}
                onClick={() => setSelectedLot(lot)}
              >
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{lot.numero_lot}</p>
                      <p className="text-sm text-muted-foreground">{lot.variete} • {lot.fournisseur_nom}</p>
                    </div>
                    <Badge>{lot.poids_brut_kg} kg</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedLot && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Calibrage: {selectedLot.numero_lot}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs">Début</label>
                    <Input 
                      type="time" 
                      value={heureDebut}
                      onChange={(e) => setHeureDebut(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs">Fin</label>
                    <Input 
                      type="time"
                      value={heureFin}
                      onChange={(e) => setHeureFin(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium">Sorties par calibre (kg)</p>
                  {(calibres[selectedLot.espece] || calibres.default || ['petit', 'moyen', 'gros', 'dechets']).map(cal => (
                    <div key={cal} className="flex items-center gap-2">
                      <span className="text-sm w-20">{cal}</span>
                      <Input 
                        type="number"
                        placeholder="kg"
                        value={sorties[cal] || ''}
                        onChange={(e) => setSorties({...sorties, [cal]: parseFloat(e.target.value) || 0})}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>

                <Button className="w-full" onClick={handleCalibrage}>
                  Valider calibrage
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// Chambres Froides
const ChambresFroidesSection = ({ chambres, onAddChambre }) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium flex items-center gap-2">
          <Thermometer className="w-5 h-5" />
          Chambres Froides
        </h3>
        <Button variant="outline" size="sm" onClick={onAddChambre}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {chambres.map(chambre => (
          <Card key={chambre.id} className={chambre.alertes_actives?.length > 0 ? 'border-red-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">{chambre.nom}</h4>
                <Badge variant={chambre.taux_remplissage > 90 ? 'destructive' : 'secondary'}>
                  {chambre.taux_remplissage}%
                </Badge>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Température</span>
                  <span className="font-medium">{chambre.temperature_actuelle}°C</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Stock</span>
                  <span className="font-medium">{chambre.stock_actuel_kg} kg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Capacité</span>
                  <span className="font-medium">{chambre.capacite_kg} kg</span>
                </div>
              </div>

              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full ${chambre.taux_remplissage > 90 ? 'bg-red-500' : chambre.taux_remplissage > 70 ? 'bg-orange-500' : 'bg-green-500'}`}
                  style={{ width: `${chambre.taux_remplissage}%` }}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Tournées
const TourneesSection = ({ tournees, onNewTournee }) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium flex items-center gap-2">
          <Route className="w-5 h-5" />
          Tournées
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onNewTournee('chargement')}>
            <Truck className="w-4 h-4 mr-2" />
            Chargement
          </Button>
          <Button variant="outline" size="sm" onClick={() => onNewTournee('livraison')}>
            <Package className="w-4 h-4 mr-2" />
            Livraison
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {tournees.length === 0 ? (
          <Card className="col-span-2 p-8 text-center text-muted-foreground">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune tournée planifiée</p>
          </Card>
        ) : (
          tournees.map(t => (
            <Card key={t.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {t.type_tournee === 'chargement' ? (
                      <div className="p-1 bg-blue-500/10 rounded">
                        <Truck className="w-4 h-4 text-blue-500" />
                      </div>
                    ) : (
                      <div className="p-1 bg-green-500/10 rounded">
                        <Package className="w-4 h-4 text-green-500" />
                      </div>
                    )}
                    <span className="font-medium">{t.numero_tournee}</span>
                  </div>
                  <Badge variant={t.statut === 'terminee' ? 'default' : t.statut === 'en_cours' ? 'secondary' : 'outline'}>
                    {t.statut}
                  </Badge>
                </div>
                
                <div className="text-sm space-y-1">
                  <p><span className="text-muted-foreground">Date:</span> {t.date_tournee}</p>
                  <p><span className="text-muted-foreground">Points:</span> {t.nb_points}</p>
                  <p><span className="text-muted-foreground">Poids prévu:</span> {t.poids_total_prevu_kg} kg</p>
                  {t.chauffeur_nom && <p><span className="text-muted-foreground">Chauffeur:</span> {t.chauffeur_nom}</p>}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Page principale
export default function ExpediteurPage() {
  const { token, selectedSociete } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [dashboardData, setDashboardData] = useState({ stats: {}, alertes: [] });
  const [fournisseurs, setFournisseurs] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [chambres, setChambres] = useState([]);
  const [tournees, setTournees] = useState([]);
  const [calibres, setCalibres] = useState({});
  
  // Modal states
  const [showFournisseurForm, setShowFournisseurForm] = useState(false);
  const [showReceptionForm, setShowReceptionForm] = useState(false);

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Societe-Id': selectedSociete?.id || ''
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const [dashRes, fournRes, recepRes, chambresRes, tourneesRes] = await Promise.all([
        fetch(`${API_URL}/api/expediteur/dashboard`, { headers }),
        fetch(`${API_URL}/api/expediteur/fournisseurs`, { headers }),
        fetch(`${API_URL}/api/expediteur/receptions`, { headers }),
        fetch(`${API_URL}/api/expediteur/chambres-froides`, { headers }),
        fetch(`${API_URL}/api/expediteur/tournees`, { headers })
      ]);

      if (dashRes.ok) setDashboardData(await dashRes.json());
      if (fournRes.ok) {
        const data = await fournRes.json();
        setFournisseurs(data.fournisseurs || []);
      }
      if (recepRes.ok) {
        const data = await recepRes.json();
        setReceptions(data.receptions || []);
      }
      if (chambresRes.ok) {
        const data = await chambresRes.json();
        setChambres(data.chambres || []);
      }
      if (tourneesRes.ok) {
        const data = await tourneesRes.json();
        setTournees(data.tournees || []);
      }

      // Charger les calibres standards
      const calibresRes = await fetch(`${API_URL}/api/expediteur/calibres-standards/abricot`, { headers });
      if (calibresRes.ok) {
        setCalibres({ abricot: (await calibresRes.json()).calibres });
      }

    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedSociete]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddFournisseur = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/expediteur/fournisseurs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success('Fournisseur créé');
        setShowFournisseurForm(false);
        fetchData();
      } else {
        toast.error('Erreur création fournisseur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleAddReception = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/expediteur/receptions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Lot ${result.numero_lot} réceptionné`);
        setShowReceptionForm(false);
        fetchData();
      } else {
        toast.error('Erreur réception');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleCalibrage = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/expediteur/calibrages`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Calibrage terminé: ${result.stats?.calibre_dominant} dominant`);
        fetchData();
      } else {
        toast.error('Erreur calibrage');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="expediteur-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-7 h-7 text-orange-500" />
            Module Expéditeur
          </h1>
          <p className="text-muted-foreground">Station fruitière & mise en marché</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Tableau</span>
          </TabsTrigger>
          <TabsTrigger value="fournisseurs" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Fournisseurs</span>
          </TabsTrigger>
          <TabsTrigger value="receptions" className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Réceptions</span>
          </TabsTrigger>
          <TabsTrigger value="calibrage" className="flex items-center gap-1">
            <Scale className="w-4 h-4" />
            <span className="hidden sm:inline">Calibrage</span>
          </TabsTrigger>
          <TabsTrigger value="frigos" className="flex items-center gap-1">
            <Thermometer className="w-4 h-4" />
            <span className="hidden sm:inline">Frigos</span>
          </TabsTrigger>
          <TabsTrigger value="tournees" className="flex items-center gap-1">
            <Truck className="w-4 h-4" />
            <span className="hidden sm:inline">Tournées</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <DashboardExpediteur stats={dashboardData.stats} alertes={dashboardData.alertes} />
        </TabsContent>

        <TabsContent value="fournisseurs" className="mt-6">
          <Dialog open={showFournisseurForm} onOpenChange={setShowFournisseurForm}>
            <FournisseursList 
              fournisseurs={fournisseurs}
              onSelect={(f) => console.log('Selected:', f)}
              onAdd={() => setShowFournisseurForm(true)}
            />
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Nouveau Fournisseur</DialogTitle>
              </DialogHeader>
              <FournisseurForm 
                onSave={handleAddFournisseur}
                onCancel={() => setShowFournisseurForm(false)}
              />
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="receptions" className="mt-6">
          <Dialog open={showReceptionForm} onOpenChange={setShowReceptionForm}>
            <ReceptionsList 
              receptions={receptions}
              onNewReception={() => setShowReceptionForm(true)}
            />
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Nouvelle Réception</DialogTitle>
              </DialogHeader>
              <ReceptionForm 
                fournisseurs={fournisseurs}
                onSave={handleAddReception}
                onCancel={() => setShowReceptionForm(false)}
              />
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="calibrage" className="mt-6">
          <CalibrageSection 
            receptions={receptions}
            calibres={calibres}
            onCalibrage={handleCalibrage}
          />
        </TabsContent>

        <TabsContent value="frigos" className="mt-6">
          <ChambresFroidesSection 
            chambres={chambres}
            onAddChambre={() => toast.info('Formulaire chambre froide à venir')}
          />
        </TabsContent>

        <TabsContent value="tournees" className="mt-6">
          <TourneesSection 
            tournees={tournees}
            onNewTournee={(type) => toast.info(`Nouvelle tournée ${type} à venir`)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
